import styled from 'styled-components'

// Customer Detail dashboard. Mirrors the Company Detail wrapper (same header /
// stat-tile / two-column body vocabulary) with a circular person avatar, an
// integrated tab bar, a wallet block, and a merge (รวมบริษัท) form.
const CustomerDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .card {
    background: ${(props) => props.theme.tokens.surface};
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .card--padded {
    padding: 24px 24px 0;
  }

  .card--flush {
    overflow: hidden;
  }

  /* ---- header ---- */
  .header-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  .header-identity {
    display: flex;
    gap: 16px;
    min-width: 0;
  }

  .avatar {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${(props) => props.theme.tokens.highlightInfo};
    color: ${(props) => props.theme.tokens.accent};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .identity-lines {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  .customer-name-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .customer-name {
    font-size: 24px;
    font-weight: 600;
    color: ${(props) => props.theme.tokens.textPrimary};
  }

  .customer-meta {
    font-size: 13px;
    color: ${(props) => props.theme.tokens.textMuted};
  }

  .customer-meta .num {
    color: ${(props) => props.theme.tokens.textPrimary};
    font-variant-numeric: tabular-nums;
  }

  .customer-meta .sep {
    margin: 0 6px;
    opacity: 0.5;
  }

  .contact-line {
    display: flex;
    gap: 20px;
    font-size: 13px;
    color: ${(props) => props.theme.tokens.textSecondary};
    flex-wrap: wrap;
  }

  .contact-line .num {
    font-variant-numeric: tabular-nums;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }

  /* ---- stats ---- */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
    margin-top: 20px;
  }

  .stat-tile {
    border: 1px solid ${(props) => props.theme.tokens.borderSubtle};
    border-radius: 8px;
    padding: 12px 16px;
  }

  .stat-label {
    font-size: 13px;
    color: ${(props) => props.theme.tokens.textMuted};
  }

  .stat-value {
    font-size: 22px;
    font-weight: 600;
    margin-top: 2px;
    font-variant-numeric: tabular-nums;
    color: ${(props) => props.theme.tokens.textPrimary};
  }

  .stat-sub {
    font-size: 12px;
    color: ${(props) => props.theme.tokens.textMuted};
    margin-top: 2px;
  }

  /* ---- tab bar (integrated into the header card) ---- */
  .detail-tabs {
    margin-top: 16px;
  }

  .detail-tabs .ant-tabs-nav {
    margin: 0;
  }

  .detail-tabs .ant-tabs-nav::before {
    border-bottom: 1px solid ${(props) => props.theme.tokens.borderSubtle};
  }

  /* content area lives in its own cards; drop antd's default padding */
  .detail-tabs .ant-tabs-content-holder {
    padding-top: 16px;
  }

  /* ---- two-column overview body ---- */
  .body-grid {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 16px;
    align-items: start;
  }

  @media (max-width: 1100px) {
    .body-grid {
      grid-template-columns: 1fr;
    }
  }

  .body-col {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
  }

  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 16px;
    border-bottom: 1px solid ${(props) => props.theme.tokens.borderSubtle};
  }

  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: ${(props) => props.theme.tokens.textPrimary};
  }

  .section-count {
    font-size: 13px;
    font-weight: 400;
    color: ${(props) => props.theme.tokens.textMuted};
    margin-left: 6px;
  }

  /* ---- wallet block ---- */
  .wallet-body {
    padding: 16px;
  }

  .wallet-label {
    font-size: 13px;
    color: ${(props) => props.theme.tokens.textMuted};
  }

  .wallet-amount {
    font-size: 24px;
    font-weight: 600;
    color: ${(props) => props.theme.tokens.textPrimary};
    font-variant-numeric: tabular-nums;
  }

  /* ---- projects list ---- */
  .project-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
    border-bottom: 1px solid ${(props) => props.theme.tokens.borderSubtle};
  }

  .project-row:last-child {
    border-bottom: none;
  }

  .project-row-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .project-name {
    font-size: 14px;
    font-weight: 500;
  }

  .project-row-bottom {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: ${(props) => props.theme.tokens.textMuted};
  }

  .project-row-bottom .num {
    color: ${(props) => props.theme.tokens.textPrimary};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /* ---- activity feed ---- */
  .activity-list {
    display: flex;
    flex-direction: column;
    padding: 8px 0;
  }

  .activity-row {
    display: flex;
    gap: 10px;
    padding: 8px 16px;
  }

  .activity-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-top: 5px;
    flex-shrink: 0;
  }

  .activity-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .activity-text {
    font-size: 13px;
    color: ${(props) => props.theme.tokens.textPrimary};
  }

  .activity-when {
    font-size: 12px;
    color: ${(props) => props.theme.tokens.textMuted};
  }

  /* ---- merge form ---- */
  .merge-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
  }

  .merge-hint {
    font-size: 12px;
    color: ${(props) => props.theme.tokens.textMuted};
  }

  .merge-row {
    display: flex;
    gap: 8px;
  }

  .empty {
    padding: 16px;
    font-size: 13px;
    color: ${(props) => props.theme.tokens.textMuted};
  }

  .cell-secondary {
    font-size: 12px;
    color: ${(props) => props.theme.tokens.textSecondary};
  }

  .num {
    font-variant-numeric: tabular-nums;
  }
`

export default CustomerDetailWrapper
